import React, {useCallback, useContext} from 'react';
import {useDispatch} from 'react-redux';

import {ProfileMain} from 'src/components/profile-tab-structure/profile-main';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {clearAccountProfileTab} from 'src/modules/profile/account/tabs/account-profile/services/account-profile-slice';
import {getAccountHierarchyTree} from 'src/openapi/admin/apis/AccountHierarchyApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {AccountSettingsController} from './account-settings-controller';

import type {MessageContextType} from 'src/contexts/message-context';


type Props = {
    accountId: number,
};

export const AccountSettingsTab: React.FC<Props> = ({accountId}) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {accountSubmit, accountFieldChange} = useEventTracking();

    const {setSuccessMessage} = useContext<MessageContextType>(MessageContext);
    const {canEditAccount} = useAdminAccessRight();

    const onProceed = useCallback(() => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        dispatch(clearAccountProfileTab());
        apiDispatch(getAccountHierarchyTree({accountId}));
    }, [accountId, apiDispatch, dispatch, setSuccessMessage]);

    const onFieldChange = (section: string) => (parameterName: string) => {
        accountFieldChange({
            parameterName,
            section,
            viewType: 'Existed',
        });
    };

    return (
        <ValidationContextProvider>
            <AccountSettingsController {...{
                accountId,
                onProceed,
                isAccountEdit: true,
                onFieldChange,
                disabled: !canEditAccount(accountId)}}>
                {({isActionsDisabled, content, saveBusy, onSave, onDiscard}) => (
                    <ProfileMain header={(
                        <Item grow={1}>
                            <Group justify="space-between" alignItems="center">
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    Settings
                                </Typography>
                                {canEditAccount(accountId) && (
                                    <Group>
                                        <Button
                                            variant="Type.SECONDARY"
                                            onClick={() => {
                                                onDiscard();
                                                accountSubmit({place: 'Settings', submit: false});
                                            }}
                                            disabled={isActionsDisabled}>
                                            Discard Changes
                                        </Button>
                                        <Button
                                            busy={saveBusy}
                                            variant="Type.PRIMARY"
                                            onClick={() => {
                                                onSave();
                                                accountSubmit({place: 'Settings', submit: true});
                                            }}
                                            disabled={isActionsDisabled}>
                                            Save Changes
                                        </Button>
                                    </Group>
                                )}
                            </Group>
                        </Item>
                    )}>
                        {content}
                    </ProfileMain>
                )}
            </AccountSettingsController>
        </ValidationContextProvider>
    );
};
